import { useEffect, useState } from "react";
import { AlertTriangle, BrainCircuit, GitBranch, HelpCircle, Layers3, Loader2, RotateCcw, Send } from "lucide-react";
import { API } from "@/lib/api";

const DEFAULT_MESSAGE = "My cofounder agrees with everything I say. He is probably incompetent.";
const labelClass = "block font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)] mb-2";
const fieldClass = "w-full bg-transparent border border-[var(--border)] px-3 py-2 font-mono-ui text-xs outline-none focus:border-[var(--accent)]";
const cmdClass = "px-4 py-3 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] font-mono-ui text-[10px] tracking-[0.24em] uppercase inline-flex items-center justify-center gap-2 disabled:opacity-50";

export default function AdminDynamicHBMSection() {
  const [health, setHealth] = useState(null);
  const [conversationId, setConversationId] = useState("admin-dynamic-sandbox");
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [mode, setMode] = useState("dynamic");
  const [mainEngine, setMainEngine] = useState("");
  const [analyzerEngine, setAnalyzerEngine] = useState("");
  const [resetState, setResetState] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const loadHealth = async () => {
    const res = await fetch(`${API}/admin/dynamic-hbm/health`, { credentials: "include" });
      if (!res.ok) throw new Error(`Health HTTP ${res.status}`);
    setHealth(await res.json());
  };

  useEffect(() => { loadHealth().catch((e) => setError(e.message)); }, []);

  const run = async () => {
    setBusy(true);
    setError("");
    try {
      const body = {
        conversation_id: conversationId,
        message,
        mode,
        reset_state: resetState,
        debug: true,
      };
      if (mainEngine.trim()) body.main_engine = mainEngine.trim();
      if (analyzerEngine.trim()) body.analyzer_engine = analyzerEngine.trim();
      const res = await fetch(`${API}/admin/dynamic-hbm/evaluate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Evaluate HTTP ${res.status}`);
      setResult(data);
      setResetState(false);
      await loadHealth();
    } catch (e) {
      setError(e.message || "Extended Thinking evaluation failed");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/admin/dynamic-hbm/state/${encodeURIComponent(conversationId)}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Reset HTTP ${res.status}`);
      setResult(null);
      await loadHealth();
    } catch (e) {
      setError(e.message || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-dynamic-hbm" className="space-y-5">
      <div className="grid md:grid-cols-4 gap-3">
        <Tile testid="dynamic-hbm-health" label="Controller" value={health?.feature || "v3.1"} sub={health?.admin_only ? "Admin only" : "—"} />
        <Tile testid="dynamic-hbm-analyzer-mode" label="Analyzer" value={health?.analyzer_mode || "always"} sub="turn-delta every run" />
        <Tile testid="dynamic-hbm-capture-mode" label="Capture" value={health?.capture_mode || "always"} sub="verified response state" />
        <Tile testid="dynamic-hbm-state-count" label="Cases" value={String(health?.state_count ?? 0)} sub="sandbox states" />
      </div>

      {error && <div data-testid="dynamic-hbm-error" className="border border-red-500/60 text-red-500 p-3 text-sm">{error}</div>}

      <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-5">
        <div className="border border-[var(--border)] p-4 space-y-4">
          <Input id="conversation-id" label="Conversation id" value={conversationId} onChange={setConversationId} />
          <div className="grid grid-cols-2 gap-3">
            <label>
              <span className={labelClass}>Mode</span>
              <select data-testid="dynamic-hbm-mode" value={mode} onChange={(e) => setMode(e.target.value)} className={fieldClass}>
                <option value="compare">Compare</option>
                <option value="dynamic">Dynamic only</option>
                <option value="baseline">Baseline only</option>
              </select>
            </label>
            <label className="flex items-end gap-2 pb-3 font-mono-ui text-[10px] tracking-[0.22em] uppercase">
              <input data-testid="dynamic-hbm-reset-state" type="checkbox" checked={resetState} onChange={(e) => setResetState(e.target.checked)} /> Reset before run
            </label>
          </div>
          <Input id="main-engine" label="Main engine override optional" value={mainEngine} onChange={setMainEngine} placeholder="e.g. avian_deepseek" />
          <Input id="analyzer-engine" label="Analyzer engine override optional" value={analyzerEngine} onChange={setAnalyzerEngine} placeholder="blank = main/active" />
          <label>
            <span className={labelClass}>Prompt</span>
            <textarea data-testid="dynamic-hbm-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={8} className={`${fieldClass} resize-y`} />
          </label>
          <div className="flex gap-3">
            <button data-testid="dynamic-hbm-run" onClick={run} disabled={busy || !message.trim()} className={`${cmdClass} flex-1`}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Run Extended Thinking
            </button>
            <button data-testid="dynamic-hbm-clear-state" onClick={clear} disabled={busy} className={cmdClass}>
              <RotateCcw size={14} /> Reset
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {!result ? (
            <div data-testid="dynamic-hbm-empty" className="border border-[var(--border)] p-8 text-center text-[var(--text-dim)] font-mono-ui text-[10px] tracking-[0.24em] uppercase">
              Run Extended Thinking to inspect final answer, turn delta, state ledger, evidence links, and next question.
            </div>
          ) : (
            <>
              <div data-testid="dynamic-hbm-meta" className="border border-[var(--border)] p-3 font-mono-ui text-[10px] tracking-[0.16em] uppercase text-[var(--text-dim)]">
                {result.main_engine} · {result.main_model} · {result.total_latency_ms}ms
              </div>
              {result.baseline && <ResponseBlock title="Baseline" testid="dynamic-hbm-baseline" data={result.baseline} />}
              {result.dynamic?.controller_output ? (
                <ControllerOutput output={result.dynamic.controller_output} latency={result.dynamic.latency_ms} />
              ) : result.dynamic && (
                <ResponseBlock title="Extended Thinking" testid="dynamic-hbm-dynamic" data={result.dynamic} />
              )}
              {result.dynamic?.decision && (
                <details data-testid="dynamic-hbm-debug" className="border border-[var(--border)] p-4">
                  <summary data-testid="dynamic-hbm-debug-toggle" className="font-mono-ui text-[10px] tracking-[0.24em] uppercase cursor-pointer">Raw controller JSON</summary>
                  <pre className="mt-3 text-xs whitespace-pre-wrap break-words max-h-80 overflow-auto">{JSON.stringify(result.dynamic.decision, null, 2)}</pre>
                </details>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Input({ id, label, value, onChange, placeholder = "" }) {
  return (
    <label>
      <span className={labelClass}>{label}</span>
      <input data-testid={`dynamic-hbm-${id}`} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={fieldClass} />
    </label>
  );
}

function Tile({ label, value, sub, testid }) {
  return <div data-testid={testid} className="border border-[var(--border)] px-4 py-3"><div className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">{label}</div><div className="text-lg font-mono-ui mt-1">{value}</div><div className="text-xs text-[var(--text-dim)] mt-1">{sub}</div></div>;
}

function ResponseBlock({ title, data, testid }) {
  return (
    <div data-testid={testid} className="border border-[var(--border)] p-4">
      <div className="flex items-center justify-between gap-3 mb-3"><h3 className="font-mono-ui text-[10px] tracking-[0.28em] uppercase">{title}</h3><span className="font-mono-ui text-[10px] text-[var(--text-dim)]">{data.latency_ms}ms</span></div>
      <div className="whitespace-pre-wrap leading-relaxed text-sm">{data.reply}</div>
      {data.state_summary && <pre className="mt-4 text-xs text-[var(--text-dim)] whitespace-pre-wrap">{JSON.stringify(data.state_summary, null, 2)}</pre>}
    </div>
  );
}

function ControllerOutput({ output, latency }) {
  const state = output.structured_state || {};
  const delta = output.turn_delta || {};
  const audit = output.controller_audit || {};
  const provider = output.provider_debug || {};
  return (
    <div data-testid="dynamic-hbm-controller-output" className="space-y-4">
      <section data-testid="dynamic-hbm-final-answer" className="border-2 border-[var(--accent)] p-5 bg-[var(--bg-glow)]">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h3 className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--accent)] inline-flex items-center gap-2">
            <BrainCircuit size={14} /> Final HBM answer
          </h3>
          <span data-testid="dynamic-hbm-latency" className="font-mono-ui text-[10px] text-[var(--text-dim)]">{latency}ms</span>
        </div>
        <p className="whitespace-pre-wrap leading-relaxed text-sm font-ui text-[var(--text)]">
          {output.final_response || "No answer returned."}
        </p>
      </section>

      <section data-testid="dynamic-hbm-inference-request" className="border border-[var(--border)] p-4">
        <SectionTitle icon={<BrainCircuit size={14} />} title="Inference request" />
        <div className="grid sm:grid-cols-4 gap-2 mb-3">
          <MiniStat testid="dynamic-hbm-provider" label="Provider" value={provider.provider || provider.engine || "—"} />
          <MiniStat testid="dynamic-hbm-model" label="Model" value={provider.model || "—"} />
          <MiniStat testid="dynamic-hbm-status" label="Status" value={provider.status || "—"} />
          <MiniStat testid="dynamic-hbm-provider-latency" label="Latency" value={provider.latency_ms ? `${provider.latency_ms}ms` : "—"} />
        </div>
        <LedgerList
          testid="dynamic-hbm-provider-diagnostics"
          empty="No provider diagnostics returned."
          items={(provider.diagnostics || []).map((d, idx) => ({ id: `D-${idx + 1}`, title: d }))}
        />
        {provider.validation?.warnings?.length > 0 && (
          <p data-testid="dynamic-hbm-validation-warning" className="font-ui text-sm text-[var(--danger)] mt-3">
            Validation warnings: {provider.validation.warnings.join(", ")}
          </p>
        )}
      </section>

      <section data-testid="dynamic-hbm-turn-delta" className="border border-[var(--border)] p-4">
        <SectionTitle icon={<GitBranch size={14} />} title="Turn delta" />
        <p data-testid="dynamic-hbm-turn-delta-summary" className="font-ui text-sm leading-relaxed text-[var(--text)] mb-3">
          {delta.summary || "No material state change captured."}
        </p>
        <div className="grid sm:grid-cols-3 gap-2 mb-4">
          <MiniStat testid="dynamic-hbm-stage-before" label="Before" value={delta.stage_before || "—"} />
          <MiniStat testid="dynamic-hbm-stage-after" label="After" value={delta.stage_after || "—"} />
          <MiniStat testid="dynamic-hbm-discourse-act" label="Act" value={delta.discourse_act || "—"} />
        </div>
        <LedgerList
          testid="dynamic-hbm-delta-observations"
          empty="No new observations"
          items={(delta.new_observations || []).map((o) => ({ id: o.id || o.temp_id, title: o.text, meta: [o.actor, o.timeframe, `confidence ${o.confidence}`].filter(Boolean).join(" · ") }))}
        />
        <LedgerList
          testid="dynamic-hbm-delta-retractions"
          empty="No retractions"
          items={(delta.retracted_observation_ids || []).map((id) => ({ id, title: id, meta: "Retracted observation" }))}
        />
        <LedgerList
          testid="dynamic-hbm-delta-contradictions"
          empty="No new contradictions"
          items={(delta.new_contradictions || []).map((c, idx) => ({ id: `C-new-${idx + 1}`, title: c.description, meta: [c.left_ref, c.right_ref].filter(Boolean).join(" ↔ ") }))}
        />
      </section>

      <section data-testid="dynamic-hbm-active-modules" className="border border-[var(--border)] p-4">
        <SectionTitle icon={<Layers3 size={14} />} title="Active reasoning modules" />
        {(output.active_reasoning_modules || []).length === 0 ? (
          <p data-testid="dynamic-hbm-no-modules" className="font-ui text-sm text-[var(--text-muted)]">
            No temporary module was active. The controller kept the answer on the base HBM kernel and current evidence.
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {output.active_reasoning_modules.map((m) => (
              <div key={m.id} data-testid={`dynamic-hbm-module-${m.id}`} className="border border-[var(--border)] p-3">
                <div className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--accent)]">{m.label}</div>
                <p className="font-ui text-sm text-[var(--text-muted)] leading-relaxed mt-2">{m.reason}</p>
                <div className="font-mono-ui text-[9px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-2">
                  {m.source || "controller"}{typeof m.confidence === "number" ? ` · ${Math.round(m.confidence * 100)}%` : ""}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section data-testid="dynamic-hbm-hypothesis-movement" className="border border-[var(--border)] p-4">
        <SectionTitle icon={<AlertTriangle size={14} />} title="Hypothesis movement" />
        <LedgerList
          testid="dynamic-hbm-hypothesis-movement-list"
          empty="No hypothesis changed this turn"
          items={(output.hypothesis_movement || []).map((h) => ({
            id: h.id,
            title: h.claim,
            meta: `${h.direction} · ${h.status_before || "new"} → ${h.status_after} · ${h.origin}`,
          }))}
        />
      </section>

      <section data-testid="dynamic-hbm-evidence-links" className="border border-[var(--border)] p-4">
        <SectionTitle icon={<GitBranch size={14} />} title="Evidence links" />
        <LedgerList
          testid="dynamic-hbm-evidence-link-list"
          empty="No explicit evidence links yet"
          items={(output.evidence_links || []).map((l, idx) => ({
            id: `${l.hypothesis_id}-${l.observation_id}-${idx}`,
            title: `${l.observation_id} ${l.relation} ${l.hypothesis_id}`,
            meta: `${l.observation || "Observation text unavailable"} · weight ${l.weight}`,
          }))}
        />
      </section>

      <section data-testid="dynamic-hbm-next-question" className="border border-[var(--border)] p-4">
        <SectionTitle icon={<HelpCircle size={14} />} title="Next diagnostic question" />
        <p className="font-ui text-sm leading-relaxed text-[var(--text)]">
          {output.next_diagnostic_question?.text || "No open diagnostic question is currently necessary."}
        </p>
      </section>

      <section data-testid="dynamic-hbm-structured-state" className="border border-[var(--border)] p-4">
        <SectionTitle icon={<Layers3 size={14} />} title="Structured case state" />
        <div className="grid gap-3">
          <StateBucket testid="dynamic-hbm-state-observations" prefix="O" title="Observations" items={state.observations} render={(o) => ({ id: o.id, title: o.text, meta: `turn ${o.source_turn} · ${o.status} · confidence ${o.confidence}` })} />
          <StateBucket testid="dynamic-hbm-state-interpretations" prefix="I" title="Interpretations" items={state.interpretations} render={(i) => ({ id: i.id, title: i.text, meta: `${i.source} · evidence ${(i.supporting_observation_ids || []).join(", ") || "none"}` })} />
          <StateBucket testid="dynamic-hbm-state-hypotheses" prefix="H" title="Hypotheses" items={state.hypotheses} render={(h) => ({ id: h.id, title: h.claim, meta: `${h.origin} · ${h.status} · + ${(h.supporting_observation_ids || []).join(", ") || "none"} · − ${(h.contradicting_observation_ids || []).join(", ") || "none"}` })} />
          <StateBucket testid="dynamic-hbm-state-claims" prefix="A" title="HBM claims" items={state.assistant_claims} render={(a) => ({ id: a.id, title: a.text, meta: `${a.claim_type} · ${a.status}${a.linked_hypothesis_id ? ` · ${a.linked_hypothesis_id}` : ""}` })} />
          <StateBucket testid="dynamic-hbm-state-contradictions" prefix="C" title="Contradictions" items={state.contradictions} render={(c) => ({ id: c.id, title: c.description, meta: `${c.status} · ${[c.left_observation_id, c.right_observation_id].filter(Boolean).join(" ↔ ") || "unlinked"}` })} />
          <StateBucket testid="dynamic-hbm-state-judgements" prefix="J" title="Judgements / rejections" items={state.judgements} render={(j) => ({ id: j.id, title: j.reason, meta: `${j.status} · ${j.hypothesis_id} capped at ${j.ceiling}` })} />
          <StateBucket testid="dynamic-hbm-state-questions" prefix="Q" title="Unresolved questions" items={state.unresolved_questions} render={(q) => ({ id: q.id, title: q.text, meta: `${q.status} · priority ${q.priority}` })} />
        </div>
      </section>

      <section data-testid="dynamic-hbm-controller-audit" className="border border-[var(--border)] p-4">
        <SectionTitle icon={<BrainCircuit size={14} />} title="Controller audit" />
        <div className="grid sm:grid-cols-2 gap-2">
          <MiniStat testid="dynamic-hbm-analyzer-ok" label="Analyzer" value={audit.analyzer_ok ? "ok" : audit.analyzer_error || "failed"} />
          <MiniStat testid="dynamic-hbm-capture-ok" label="Capture" value={audit.capture_ok ? "ok" : audit.capture_error || "failed"} />
          <MiniStat testid="dynamic-hbm-modules-inherited" label="Inherited" value={audit.modules_inherited ? "yes" : "no"} />
          <MiniStat testid="dynamic-hbm-state-change-counts" label="State changes" value={Object.values(audit.state_changes || {}).reduce((a, b) => a + Number(b || 0), 0)} />
        </div>
      </section>
    </div>
  );
}

function SectionTitle({ icon, title }) {
  return (
    <h3 className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--accent)] inline-flex items-center gap-2 mb-3">
      {icon}
      {title}
    </h3>
  );
}

function MiniStat({ label, value, testid }) {
  return (
    <div data-testid={testid} className="border border-[var(--border)] px-3 py-2 min-w-0">
      <div className="font-mono-ui text-[9px] tracking-[0.2em] uppercase text-[var(--text-dim)]">{label}</div>
      <div className="font-mono-ui text-xs text-[var(--text)] mt-1 truncate">{String(value ?? "—")}</div>
    </div>
  );
}

function LedgerList({ items, empty, testid }) {
  if (!items || items.length === 0) {
    return <p data-testid={`${testid}-empty`} className="font-ui text-sm text-[var(--text-muted)]">{empty}</p>;
  }
  return (
    <div data-testid={testid} className="space-y-2">
      {items.map((item) => (
        <div key={item.id} data-testid={`${testid}-item-${item.id}`} className="border-l-2 border-[var(--border)] pl-3 py-1">
          <div className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text)] break-all">{item.id}</div>
          <p className="font-ui text-sm text-[var(--text)] leading-relaxed mt-1 whitespace-pre-wrap">{item.title || "—"}</p>
          {item.meta && <p className="font-mono-ui text-[9px] tracking-[0.16em] uppercase text-[var(--text-dim)] mt-1 break-all">{item.meta}</p>}
        </div>
      ))}
    </div>
  );
}

function StateBucket({ title, prefix, items, render, testid }) {
  const visible = items || [];
  return (
    <details data-testid={testid} className="border border-[var(--border)] p-3" open={prefix === "O" || prefix === "H"}>
      <summary data-testid={`${testid}-toggle`} className="font-mono-ui text-[10px] tracking-[0.24em] uppercase cursor-pointer text-[var(--text)]">
        {prefix}-* · {title} · {visible.length}
      </summary>
      <div className="mt-3">
        <LedgerList
          testid={`${testid}-list`}
          empty={`No ${title.toLowerCase()} in active prompt state`}
          items={visible.map(render)}
        />
      </div>
    </details>
  );
}